import React from 'react';
import { createChainedFunction } from '@atc/create-chained-function';
import { useSelector, useDispatch } from 'react-redux';

import clsx from 'clsx';

import { sendClick } from 'reaxl-analytics';
import {
    contactDealerClick,
    creditAppFollowupClick,
    creditAppGetPreQualifiedClick,
    editDealerOfferCTAClick,
    telMetricsPhoneTextClick,
    textLinkDealClick,
} from 'reaxl-analytics-handlers';
import { useFeatures } from '@atc/features';

import { Panel, Label, LoadingSpinner, Link } from 'reaxl';
import { Button, Link as LinkButton } from '@iris/react-components';

import {
    CLOSE_WALLET,
    WALLET_EMAIL_DEALER_REQUEST,
    WALLET_UPDATE_DEAL_NOTIFICATION_REQUEST,
    useWalletCalcConfig,
} from 'wallet-manager';

import { eventBus } from '@atc/cai-event-bus';

import _isEmpty from 'lodash/isEmpty';
import DealCardVehicleOverview from './DealCardVehicleOverview';
import DealCardOfferDetails from './DealCardOfferDetails';
import DealCardVehicleProtection from './DealCardVehicleProtection';
import DealCardDeepLink from './DealCardDeepLink';

const pipe = <span className="padding-horizontal-2">|</span>;
const testDriveDeeplink = (vdpUrl, onScheduleTestDriveClick, label) => (
    <Link
        href={vdpUrl}
        onClick={(event) => onScheduleTestDriveClick(event, label)}
        label={label}
        undecorated
    />
);

const getTestDriveStatus = ({ testDriveCompleted, scheduledDate }) => {
    if (testDriveCompleted) {
        return 'completed';
    }

    if (scheduledDate) {
        return 'submitted';
    }

    return null;
};

// Ensure status after submitted
const getCreditDecisionStatus = ({ dealerDecisionStatus = '', creditAppSubmittedFlag = false }) => {
    if (dealerDecisionStatus) {
        // some credit decisions come in as camel case and we add a space using the split/regex/join combo
        return dealerDecisionStatus === 'Declined'
            ? 'Needs Attention'
            : dealerDecisionStatus?.split(/(?=[A-Z])/).join(' ');
    }
    if (creditAppSubmittedFlag) {
        return 'Submitted';
    }

    return null;
};

const tableData = {
    creditPreApproval: {
        label: 'Credit Pre-Approval',
        noStatus: (props) => (
            <DealCardDeepLink
                {...props}
                buttonText="Apply for Financing"
                route="credit"
            />
        ),
        postStatus: (props, onEmailDealerClick) => (
            <LinkButton
                variant="standalone"
                onClick={(event) => onEmailDealerClick(event, 'walletCreditApp')}
            >
                Follow-up
            </LinkButton>
        ),
    },
    sacaApplication: {
        label: 'Credit Pre-Approval',
        noStatus: ({ handleSACAApplicationClick, isVdp, ...rest }) => (
            <LinkButton
                variant="standalone"
                onClick={() => handleSACAApplicationClick()}
            >
                Apply for Financing
            </LinkButton>
        ),
        postStatus: ({ handleSACAFollowUpClick, ...rest }) => (
            <LinkButton
                variant="standalone"
                onClick={() => handleSACAFollowUpClick()}
            >
                Follow-up
            </LinkButton>
        ),
    },
    testDrive: {
        label: 'Test Drive',
        noStatus: ({ isAccelerate, vdpUrl, onScheduleTestDriveClick, isCompactView, ...rest }) => (
            <>
                {isAccelerate && (
                    <DealCardDeepLink
                        {...rest}
                        buttonText="Schedule"
                        route="testDrive"
                    />
                )}
                {!isAccelerate &&
                    testDriveDeeplink(
                        vdpUrl,
                        onScheduleTestDriveClick,
                        isCompactView ? 'Schedule Test Drive' : 'Schedule'
                    )}
            </>
        ),
        postStatus: ({
            testDriveStatus,
            onTestDriveMarkDoneClick,
            isAccelerate,
            vdpUrl,
            onScheduleTestDriveClick,
            ...rest
        }) => {
            const hasBeenCompleted = testDriveStatus === 'completed';
            const buttonText = hasBeenCompleted ? 'Schedule Another Visit' : 'Reschedule';

            return (
                <>
                    {!hasBeenCompleted && (
                        <>
                            <LinkButton
                                variant="standalone"
                                className="iris:inline"
                                onClick={onTestDriveMarkDoneClick}
                            >
                                Mark as done
                            </LinkButton>
                            {pipe}
                        </>
                    )}
                    {isAccelerate && (
                        <DealCardDeepLink
                            {...rest}
                            buttonText={buttonText}
                            route="testDrive"
                        />
                    )}
                    {!isAccelerate && testDriveDeeplink(vdpUrl, onScheduleTestDriveClick, buttonText)}
                </>
            );
        },
    },
};

const renderTableRow = (
    key,
    status,
    props,
    onEmailDealerClick,
    vehicleProtectionProgram,
    isReceiptView = false,
    isCompactView = false
) => {
    // 1. hide <hr /> when isReceiptView is true
    // 2. always show <hr /> after creditPreApproval, since testDrive always exists
    // 3. show <hr /> after testDrive when vehicleProtectionProgram shows up
    const showHrElement =
        !isReceiptView &&
        (key === 'creditPreApproval' ||
            key === 'sacaApplication' ||
            (key === 'testDrive' && vehicleProtectionProgram && props.isDr));
    return (
        <>
            <div
                className={clsx(
                    { 'margin-bottom-2': (isReceiptView && key === 'creditPreApproval') || key === 'sacaApplication' },
                    'display-flex justify-content-between',
                    { 'margin-bottom-4': isCompactView }
                )}
            >
                <div className="display-flex align-items-center">
                    <div className="text-bold">{tableData[key]?.label}</div>
                    {status && (
                        <Label
                            data-cmp="dealStatus"
                            bsStyle="default"
                            className="margin-horizontal-2"
                        >
                            {status}
                        </Label>
                    )}
                </div>
                <div
                    className="text-right"
                    data-cmp={`deal-${key}`}
                >
                    {status
                        ? tableData[key]?.postStatus(props, onEmailDealerClick)
                        : tableData[key]?.noStatus({ ...props, isCompactView })}
                </div>
            </div>
            {showHrElement && <hr />}
        </>
    );
};

function PanelWrapper({ children, isReceiptView }) {
    return !isReceiptView ? (
        <Panel data-cmp="dealCard">
            <Panel.Body>{children}</Panel.Body>
        </Panel>
    ) : (
        <div data-cmp="dealCard">{children}</div>
    );
}

function DealCard({
    dealData = {},
    handleActiveWalletPanel,
    onEmailCTAClick = () => {},
    indexClicked,
    paymentsDuck,
    dealsDuck,
    uiContext,
    addListingToHideReceipt = () => {},
    onSACAApplicationClick,
    enableSaca,
    pageName,
    isLoading,
    isCompactView = false,
}) {
    const {
        active_deals_my_wallet: [, { event_bus_contact_email: eventBusContactEmail }],
    } = useFeatures(['active_deals_my_wallet']);

    const isReceiptView = uiContext === 'receipt';
    const dispatch = useDispatch();

    const paymentsInfo = useSelector(paymentsDuck.selectors.getDuckState);

    const {
        id: dealId,
        isEcommerce = false,
        isAccelerate,
        vehicle = {},
        deal = {},
        creditApp = {},
        tradeIn,
        testDrive = {},
        dealer = {},
    } = dealData;

    const { id, vdpUrl, vdpBaseUrl, esntial = false, vehicleProtectionProgram } = vehicle;

    const testDriveStatus = getTestDriveStatus(testDrive);
    const creditDecisionStatus = getCreditDecisionStatus(creditApp);

    const isCashDeal = deal?.dealType === 'Cash';
    const isVdp = pageName === 'vdp';
    const creditAppDeals =
        useSelector((state) => dealsDuck?.selectors?.getCreditAppDealsByOwnerId?.(state, dealer?.id)) || [];
    const isOwnerFinancingSubmitted = !_isEmpty(creditAppDeals);

    const isEsntialDeal = esntial && !esntial?.deemphasized && !isAccelerate && !isEcommerce;

    const onSDPError = () => {
        // Fall back to wallet lead form if sdp errors out
        handleActiveWalletPanel({ panel: 'emailForm', activeDealId: dealId, uiContext: 'wallet' });
    };

    const onTestDriveMarkDoneClick = (event) => {
        eventBus.emit(WALLET_UPDATE_DEAL_NOTIFICATION_REQUEST, { dealId, body: { testDriveCompleted: true } });
        dispatch(
            dealsDuck?.creators?.updateDealReminders({
                dealId,
                testDriveCompleted: true,
            })
        );
        sendClick(textLinkDealClick, event, { inventoryId: listing?.id, text: 'Mark as done' });
    };

    const onEmailDealerClick = (event, emailUiContext) => {
        event.stopPropagation();

        if (eventBusContactEmail) {
            eventBus.emit(WALLET_EMAIL_DEALER_REQUEST, { dealId, vehicle, vdpUrl });
            eventBus.emit(CLOSE_WALLET);
        } else {
            handleActiveWalletPanel({ panel: 'emailForm', activeDealId: dealId, uiContext: emailUiContext });
        }

        sendClick(contactDealerClick, event, { inventoryId: id });
    };

    const onPhoneAndTextClick = (leadType) => (phoneNumber) => {
        const data = {
            dealXg: dealData,
            localStorageDeal: {
                testDriveStatus,
            },
            leadType,
            listing: vehicle,
            phoneNumber,
        };
        sendClick(telMetricsPhoneTextClick, {}, data);
    };

    const handleWalletModalClose = () => {
        dispatch(paymentsDuck.creators.updateMyWallet({ displayModal: false }));
    };

    const onScheduleTestDriveClick = (event, buttonText) => {
        handleWalletModalClose();
        sendClick(textLinkDealClick, event, {
            inventoryId: id,
            text: buttonText,
            isTestDrive: true,
        });
    };

    const getVDPUrl = (listingId, query = '') => {
        // NOTE: VDP Url could come from the vin validation process in syndicated sites (DDC sites for example)
        if (vdpUrl) {
            return vdpUrl;
        }

        return vdpBaseUrl ? `${vdpBaseUrl}${query}#${listingId}` : '';
    };

    const handleSACAApplicationClick = async (event) => {
        sendClick(creditAppGetPreQualifiedClick, event, { listingId: id, partner: 'saca' });

        if (!isVdp) {
            window.open(getVDPUrl('nativeDealContainer', '&launchCreditApp=true'), '_self');
        }
        onSACAApplicationClick();
        handleWalletModalClose();
    };

    const handleSACAFollowUpClick = (event) => {
        sendClick(creditAppFollowupClick, event, { inventoryId: id, text: 'Follow-up' });
        handleActiveWalletPanel({ panel: 'emailForm', activeDealId: dealId, uiContext: 'walletCreditApp' });
    };

    const { enableNegotiation } = useWalletCalcConfig(paymentsInfo, vehicle);

    const dealDataWithDealer = {
        ...dealData,
        dealer,
        vehicle: {
            ...dealData.vehicle,
            negotiation: enableNegotiation,
            owner: dealer, // SDP requires owner data to update dr deals
            vdpUrl: vdpUrl || vdpBaseUrl,
        },
    };

    const linkProps = {
        ...dealDataWithDealer,
        paymentsDuck,
        dealsDuck,
        onSDPError,
        paymentsInfo,
        isReceiptView,
        dealId,
    };

    const sharedProps = {
        dealData: dealDataWithDealer,
        paymentsDuck,
        dealsDuck,
        onSDPError,
        paymentsInfo,
        listingId: id,
        isReceiptView,
        isCompactView,
    };

    const handleDealReceiptOverride = () => {
        addListingToHideReceipt(true);
    };

    const fireEditDealerOfferCTAClick = (event) => {
        sendClick(editDealerOfferCTAClick, event, { inventoryId: id, isReceiptView });
    };

    const onModifyDealCTAClick = createChainedFunction(handleDealReceiptOverride, fireEditDealerOfferCTAClick);

    const enableSacaCreditApp = enableSaca && !isCashDeal && !isAccelerate && dealer?.sacaMmdId;

    if (isLoading && !isReceiptView) {
        return <LoadingSpinner className="margin-top-0" />;
    }

    return (
        <PanelWrapper isReceiptView={isReceiptView}>
            {!isReceiptView && (
                <DealCardVehicleOverview
                    dealData={dealDataWithDealer}
                    indexClicked={indexClicked}
                    onEmailDealerClick={onEmailDealerClick}
                    onPhoneClick={onPhoneAndTextClick('phone')}
                    onTextClick={onPhoneAndTextClick('text')}
                />
            )}
            <div className={clsx({ 'margin-vertical-2': !isReceiptView })}>
                <DealCardOfferDetails
                    creditApp={creditApp}
                    dealId={dealId}
                    offerDetails={deal}
                    onSDPError={onSDPError}
                    dealsDuck={dealsDuck}
                    paymentsDuck={paymentsDuck}
                    tradeIn={tradeIn}
                    vehicle={dealDataWithDealer.vehicle}
                    isAccelerate={isAccelerate}
                    vdpUrl={getVDPUrl('nativeDealContainer')}
                    isReceiptView={isReceiptView}
                    onClick={handleWalletModalClose}
                    handleDealReceiptOverride={handleDealReceiptOverride}
                    isEsntialDeal={isEsntialDeal}
                    onEmailCTAClick={isEcommerce ? onEmailCTAClick : null}
                    isCompactView={isCompactView}
                />

                {(!isEsntialDeal || isAccelerate) &&
                    !isEcommerce && ( // Show it only for DR and Non DR Deals
                        <>
                            {isReceiptView && <hr className="margin-vertical-4" />}
                            {!isCashDeal &&
                                isAccelerate &&
                                renderTableRow(
                                    'creditPreApproval',
                                    creditDecisionStatus,
                                    linkProps,
                                    onEmailDealerClick,
                                    vehicleProtectionProgram,
                                    isReceiptView,
                                    isCompactView
                                )}
                            {enableSacaCreditApp &&
                                renderTableRow(
                                    'sacaApplication',
                                    isOwnerFinancingSubmitted && 'Submitted',
                                    { ...linkProps, handleSACAApplicationClick, handleSACAFollowUpClick, isVdp },
                                    onEmailDealerClick,
                                    vehicleProtectionProgram,
                                    isReceiptView,
                                    isCompactView
                                )}
                            {renderTableRow(
                                'testDrive',
                                testDriveStatus,
                                {
                                    ...linkProps,
                                    testDriveStatus,
                                    onTestDriveMarkDoneClick,
                                    isAccelerate,
                                    vdpUrl: getVDPUrl('inquirySelection', '&inquiry=scheduleTestDrive'),
                                    onScheduleTestDriveClick,
                                },
                                null,
                                vehicleProtectionProgram,
                                isReceiptView,
                                isCompactView
                            )}
                            {isAccelerate && (
                                <>
                                    {isReceiptView && vehicleProtectionProgram && <hr className="margin-vertical-4" />}
                                    <DealCardVehicleProtection {...sharedProps} />
                                </>
                            )}
                        </>
                    )}

                {isReceiptView && isAccelerate && !isEcommerce && (
                    <DealCardDeepLink
                        {...linkProps}
                        buttonText="Modify Your Offer"
                        buttonStyle="primary"
                        buttonClassName={clsx('btn-block', { 'margin-top-5': !isCompactView })}
                    />
                )}

                {isReceiptView && !isAccelerate && !isEsntialDeal && !isEcommerce && (
                    <Button
                        className={clsx('iris:block', 'iris:w-full', { 'iris:mt-6': !isCompactView })}
                        onClick={onModifyDealCTAClick}
                    >
                        Modify Your Offer
                    </Button>
                )}
            </div>
        </PanelWrapper>
    );
}

export default DealCard;
